import React, { useState, useEffect } from 'react';
import { AlertTriangle, XCircle, Info, CreditCard, FileText, Mail, ChevronRight, Shield, Clock, Ban, CheckCircle } from 'lucide-react';
import { getText, getCurrentLanguage } from '../services/dataSync';

const RefundPolicyPage = () => {
  const currentYear = new Date().getFullYear();
  const lastUpdated = '2024-01-15';
  const [expandedSections, setExpandedSections] = useState({});
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [sections, setSections] = useState([]);

  // Update sections when language changes
  const updateSections = () => {
    setSections([
      { id: 'introduction', title: getText('refund.section.introduction') || 'Introduction' },
      { id: 'no-refund', title: getText('refund.section.noRefund') || 'No Refund Policy' },
      { id: 'payment', title: getText('refund.section.payment') || 'Payment Processing' },
      { id: 'exceptions', title: getText('refund.section.exceptions') || 'Limited Exceptions' },
      { id: 'non-refundable', title: getText('refund.section.nonRefundable') || 'Non-Refundable Scenarios' },
      { id: 'membership', title: getText('refund.section.membership') || 'Membership Fees' },
      { id: 'events', title: getText('refund.section.events') || 'Event Registrations' },
      { id: 'disputes', title: getText('refund.section.disputes') || 'Payment Disputes' },
      { id: 'chargebacks', title: getText('refund.section.chargebacks') || 'Chargebacks' },
      { id: 'cancellation', title: getText('refund.section.cancellation') || 'Cancellation by IFM' },
      { id: 'contact', title: getText('refund.section.contact') || 'Contact for Refund Requests' }
    ]);
  };

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
      updateSections();
    };

    // Initial load
    updateSections();

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  const sectionsList = sections.length > 0 ? sections : [
    { id: 'introduction', title: getText('refund.section.introduction') || 'Introduction' },
    { id: 'no-refund', title: getText('refund.section.noRefund') || 'No Refund Policy' },
    { id: 'payment', title: getText('refund.section.payment') || 'Payment Processing' },
    { id: 'exceptions', title: getText('refund.section.exceptions') || 'Limited Exceptions' },
    { id: 'non-refundable', title: getText('refund.section.nonRefundable') || 'Non-Refundable Scenarios' },
    { id: 'membership', title: getText('refund.section.membership') || 'Membership Fees' },
    { id: 'events', title: getText('refund.section.events') || 'Event Registrations' },
    { id: 'disputes', title: getText('refund.section.disputes') || 'Payment Disputes' },
    { id: 'chargebacks', title: getText('refund.section.chargebacks') || 'Chargebacks' },
    { id: 'cancellation', title: getText('refund.section.cancellation') || 'Cancellation by IFM' },
    { id: 'contact', title: getText('refund.section.contact') || 'Contact for Refund Requests' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.scrollBy(0, -80);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        <div className="container relative z-10 py-8 md:py-12">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              {getText('refund.title') || 'Refund & Return Policy'}
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-blue-100 leading-relaxed max-w-3xl mx-auto">
              {getText('refund.subtitle') || 'Important information regarding refunds and returns for our services.'}
            </p>
            <div className="mt-6 flex items-center justify-center gap-4 text-sm text-blue-200">
              <span>{getText('privacy.lastUpdated') || 'Last Updated'}: {lastUpdated}</span>
              <span>•</span>
              <span>{getText('privacy.effectiveDate') || 'Effective Date'}: {lastUpdated}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-6 md:py-8 bg-red-50 border-b-4 border-red-600">
        <div className="container-medium">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm p-5 md:p-6 border-l-4 border-red-600">
              <div className="flex items-start gap-4">
                <AlertTriangle className="h-6 w-6 md:h-7 md:w-7 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-red-900 mb-2">{getText('refund.noRefundNotice') || 'No Refund Policy'}</h3>
                  <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                    Please note that <strong>all payments made to Industrial Fedration of Maharashtra Region (IFM) are final and non-refundable</strong>. 
                    This policy applies to membership fees, event registrations, training programs, and all other services provided by IFM.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents - Professional Design */}
      <section className="bg-white border-b border-gray-200 sticky top-16 z-30 shadow-sm">
        <div className="container-medium">
          <div className="py-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="bg-primary-100 p-2 rounded-lg">
                <FileText className="h-5 w-5 text-primary-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">{getText('refund.tableOfContents') || 'Table of Contents'}</h2>
            </div>
            <nav className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
              {(sections.length > 0 ? sections : [
                { id: 'introduction', title: getText('refund.section.introduction') || 'Introduction' },
                { id: 'no-refund', title: getText('refund.section.noRefund') || 'No Refund Policy' },
                { id: 'payment', title: getText('refund.section.payment') || 'Payment Processing' },
                { id: 'exceptions', title: getText('refund.section.exceptions') || 'Limited Exceptions' },
                { id: 'non-refundable', title: getText('refund.section.nonRefundable') || 'Non-Refundable Scenarios' },
                { id: 'membership', title: getText('refund.section.membership') || 'Membership Fees' },
                { id: 'events', title: getText('refund.section.events') || 'Event Registrations' },
                { id: 'disputes', title: getText('refund.section.disputes') || 'Payment Disputes' },
                { id: 'chargebacks', title: getText('refund.section.chargebacks') || 'Chargebacks' },
                { id: 'cancellation', title: getText('refund.section.cancellation') || 'Cancellation by IFM' },
                { id: 'contact', title: getText('refund.section.contact') || 'Contact for Refund Requests' }
              ]).map((section, index) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className="group text-left px-3 py-2.5 text-xs sm:text-sm bg-gray-50 hover:bg-primary-600 text-gray-700 hover:text-black rounded-lg transition-all duration-200 border border-gray-200 hover:border-primary-600 hover:shadow-md"
                >
                  <span className="text-primary-600 group-hover:text-black font-semibold mr-1">{index + 1}.</span>
                  <span className="font-medium">{section.title}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="section py-12 md:py-16 bg-gray-50">
        <div className="container-medium">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-8 md:p-10 lg:p-12">
            
            {/* Introduction */}
            <div id="introduction" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                {getText('refund.section.introduction') || 'Introduction'}
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
                  This Refund & Return Policy ("Policy") governs all refund and return requests for services provided by Industrial 
                  Fedration of Maharashtra Region ("IFM", "we", "us", or "our"). By making a payment for any of our services, you 
                  acknowledge that you have read, understood, and agree to this Policy.
                </p>
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                  <p className="text-base md:text-lg text-gray-700 font-semibold">
                    <strong>IMPORTANT:</strong> All payments are final and non-refundable. Please read this Policy carefully before 
                    making any payment.
                  </p>
                </div>
              </div>
            </div>

            {/* No Refund Policy */}
            <div id="no-refund" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-red-600">
                1. {getText('refund.section.noRefund') || 'No Refund Policy'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6 font-semibold">
                <strong>All payments made to IFM are final and non-refundable.</strong> This includes but is not limited to:
              </p>
              <ul className="list-none space-y-3 mb-6">
                {[
                  'Membership registration fees',
                  'Membership renewal fees',
                  'Event registration fees',
                  'Training program fees',
                  'Certification fees',
                  'Consultation fees',
                  'Documentation fees',
                  'Any other service fees'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                <p className="text-base md:text-lg text-gray-900 font-semibold">
                  Once a payment is processed and confirmed, it cannot be refunded, reversed, or cancelled under any circumstances, 
                  except as explicitly stated in this Policy.
                </p>
              </div>
            </div>

            {/* Payment Processing */}
            <div id="payment" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                2. {getText('refund.section.payment') || 'Payment Processing'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                All payments are processed through secure payment gateways including Razorpay and other authorized payment 
                processors. Once a payment is successfully processed:
              </p>
              <ul className="list-none space-y-3">
                {[
                  'The payment is immediately applied to your account or service',
                  'You will receive a confirmation email with payment details',
                  'The payment cannot be reversed or refunded',
                  'Service access or membership benefits are activated immediately'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ChevronRight className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Limited Exceptions */}
            <div id="exceptions" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                3. {getText('refund.section.exceptions') || 'Limited Exceptions'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                In extremely rare circumstances, IFM may consider a refund request only if:
              </p>
              <div className="space-y-4 mb-6">
                {[
                  { title: 'Technical Error', desc: 'Payment was processed due to a technical error on our part' },
                  { title: 'Duplicate Payment', desc: 'The same payment was processed multiple times due to system error' },
                  { title: 'Service Not Provided', desc: 'Service was not provided due to our failure or cancellation' }
                ].map((item, index) => (
                  <div key={index} className="pl-4 border-l-4 border-gray-300 hover:border-primary-500 transition-colors">
                    <h3 className="font-semibold text-gray-900 mb-2 text-lg">{item.title}</h3>
                    <p className="text-base text-gray-700">{item.desc}</p>
                  </div>
                ))}
              </div>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg mb-4">
                <p className="text-sm md:text-base text-gray-700">
                  <strong>Note:</strong> Refund requests will be reviewed on a case-by-case basis and are subject to verification. 
                  Refunds, if approved, will be processed within 15-30 business days and credited to the original payment method.
                </p>
              </div>
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                <p className="text-sm md:text-base text-gray-700">
                  <strong>Important:</strong> Refund requests must be submitted within 7 days of the payment date. Requests submitted 
                  after this period will not be considered.
                </p>
              </div>
            </div>

            {/* Non-Refundable Scenarios */}
            <div id="non-refundable" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                4. {getText('refund.section.nonRefundable') || 'Non-Refundable Scenarios'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                The following scenarios are explicitly non-refundable:
              </p>
              <ul className="list-none space-y-3">
                {[
                  'Change of mind or decision not to use the service',
                  'Inability to attend an event or training program',
                  'Dissatisfaction with service quality',
                  'Membership cancellation or withdrawal',
                  'Failure to meet eligibility requirements discovered after payment',
                  'Payment made by mistake or error on your part',
                  'Bank or payment gateway errors (contact your bank/payment provider)',
                  'Service cancellation due to circumstances beyond our control',
                  'Expired or unused service benefits'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Membership Fees */}
            <div id="membership" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                5. {getText('refund.section.membership') || 'Membership Fees'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                Membership fees are calculated based on the number of workers and are non-refundable once payment is processed. 
                This includes:
              </p>
              <ul className="list-none space-y-3 mb-6">
                {[
                  'Initial membership registration fees',
                  'Annual membership renewal fees',
                  'Additional worker fees',
                  'GST and other applicable taxes'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                Membership benefits are activated immediately upon payment confirmation and remain valid for the membership period, 
                regardless of usage.
              </p>
            </div>

            {/* Event Registrations */}
            <div id="events" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                6. {getText('refund.section.events') || 'Event Registrations'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                Event registration fees are non-refundable. This applies to:
              </p>
              <ul className="list-none space-y-3 mb-6">
                {[
                  'Conference registrations',
                  'Workshop registrations',
                  'Training program registrations',
                  'Seminar registrations',
                  'Any other event registrations'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-gray-50 border-l-4 border-gray-400 p-4 rounded-r-lg">
                <p className="text-base md:text-lg text-gray-700">
                  If you are unable to attend an event, you may transfer your registration to another person with prior written approval 
                  from IFM, subject to eligibility requirements.
                </p>
              </div>
            </div>

            {/* Payment Disputes */}
            <div id="disputes" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                7. {getText('refund.section.disputes') || 'Payment Disputes'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                If you believe there is an error in your payment or you have concerns about a transaction:
              </p>
              <div className="space-y-3 mb-6">
                {[
                  'Contact us immediately at support@mahaifm.com',
                  'Provide detailed information about the transaction',
                  'Include payment receipt and transaction ID',
                  'Allow 5-7 business days for investigation'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 pl-4 border-l-4 border-gray-300 hover:border-primary-500 transition-colors">
                    <span className="bg-primary-600 text-white rounded-full w-6 h-6 flex items-center justify-center font-bold text-sm flex-shrink-0 mt-0.5">
                      {index + 1}
                    </span>
                    <span className="text-base md:text-lg text-gray-700 pt-0.5">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                We will investigate all disputes in good faith, but please note that our No Refund Policy still applies unless 
                an exception is explicitly approved.
              </p>
            </div>

            {/* Chargebacks */}
            <div id="chargebacks" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                8. {getText('refund.section.chargebacks') || 'Chargebacks'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                Initiating a chargeback or payment reversal through your bank or payment provider does not guarantee a refund. 
                Chargebacks may result in:
              </p>
              <ul className="list-none space-y-3 mb-6">
                {[
                  'Immediate suspension of your account and services',
                  'Legal action to recover the disputed amount',
                  'Permanent ban from IFM services',
                  'Additional fees and charges'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                <p className="text-base md:text-lg text-gray-700">
                  <strong>We strongly encourage you to contact us directly to resolve any payment issues before initiating a chargeback.</strong>
                </p>
              </div>
            </div>

            {/* Cancellation by IFM */}
            <div id="cancellation" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                9. {getText('refund.section.cancellation') || 'Cancellation by IFM'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                If IFM cancels a service or event due to circumstances beyond our control (e.g., natural disasters, government 
                restrictions, force majeure), we will:
              </p>
              <ul className="list-none space-y-3 mb-6">
                {[
                  'Attempt to reschedule the service or event',
                  'Offer alternative services or credits where applicable',
                  'Consider refund requests on a case-by-case basis'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ChevronRight className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                However, refunds are not guaranteed and are subject to our discretion and available resources.
              </p>
            </div>

            {/* Contact for Refund Requests */}
            <div id="contact" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                10. {getText('refund.section.contact') || 'Contact for Refund Requests'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                If you believe you qualify for an exception under this Policy, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-lg">Industrial Fedration of Maharashtra Region</h3>
                    <p className="text-base text-gray-700">
                      Office no.9, A-wing, Jewel of Pimpri,<br />
                      Kamla Cross Road, Pimpri, Pune-411018
                    </p>
                  </div>
                  <div className="pt-4 border-t border-gray-300 space-y-2">
                    <p className="text-base text-gray-700">
                      <span className="font-semibold">Email:</span>{' '}
                      <a href="mailto:support@mahaifm.com" className="text-primary-600 hover:underline">support@mahaifm.com</a>
                    </p>
                    <p className="text-base text-gray-700">
                      <span className="font-semibold">Phone:</span>{' '}
                      <a href="tel:9067205252" className="text-primary-600 hover:underline">9067205252</a>
                    </p>
                  </div>
                  <div className="pt-4 border-t border-gray-300">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Subject Line:</p>
                    <p className="text-sm text-gray-700 mb-4">Refund Request - [Transaction ID]</p>
                    <p className="text-sm font-semibold text-gray-900 mb-2">Required Information:</p>
                    <ul className="space-y-1 text-sm text-gray-700">
                      {[
                        'Transaction ID or Payment Receipt',
                        'Date of payment',
                        'Amount paid',
                        'Reason for refund request',
                        'Supporting documentation'
                      ].map((item, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <ChevronRight className="h-4 w-4 text-primary-600 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Acknowledgment */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-gray-400">
                11. {getText('refund.section.acknowledgment') || 'Acknowledgment'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                By making a payment to IFM, you acknowledge that:
              </p>
              <ul className="list-none space-y-3">
                {[
                  'You have read and understood this Refund & Return Policy',
                  'You agree that all payments are final and non-refundable',
                  'You will not initiate chargebacks or payment reversals',
                  'You understand the terms and conditions of the service',
                  'You accept full responsibility for your payment decision'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-gray-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Effective Date */}
            <div className="border-t-2 border-gray-300 pt-8 mt-12">
              <p className="text-sm md:text-base text-gray-500 text-center">
                This Refund & Return Policy is effective as of <strong className="text-gray-700">{lastUpdated}</strong>. All payments made after this date are subject to 
                this Policy.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RefundPolicyPage;
